/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * SignInPage
 *
 * React page responsible for authenticating users against the backend
 * `/arcee/user/login/` endpoint. Features include:
 * - Submission of username/password to the API using APIHelper
 * - Inline server-side validation errors returned to the SignInForm
 * - Secure storage of returned user data (excluding message) via storageHelper
 * - Automatic redirect to `/dashboard` upon successful authentication
 *
 * The page delegates all UI rendering to `SignInForm`, while handling
 * API communication, error mapping, and navigation flow.
 */
import React from "react";
import SignInForm from "../components/SignInForm";
import APIHelper from "../api/APIHelper";
import envConfig from "../config/envConfig";
import { useNavigate, useLocation } from "react-router-dom";
import { store, retrieve } from "../utils/storageHelper";
import { isUserLoggedIn } from "../utils/utils";

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

function SignInPage() {
    const navigate = useNavigate();
    const location = useLocation();

    // Redirect to dashboard if user is already logged in
    React.useEffect(() => {
        let mounted = true;

        (async () => {
            const isLogged = await isUserLoggedIn();
            if (!mounted) return;

            if (isLogged) {
                navigate("/dashboard", { replace: true });
            }
        })();

        return () => { mounted = false; };
    }, [navigate]);

    const nextParam = React.useMemo(() => {
        try {
            const params = new URLSearchParams(location.search);
            return params.get("next") || "/dashboard";
        } catch (e) {
            return "/dashboard";
        }
    }, [location.search]);
    
    const handleSignIn = async ({ username, password }, setErrors) => {
        try {
            const response = await apiHelper.apiCall("/arcee/user/login/", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username, password }),
            });

            if (!response.ok) {
                const err = await response.json();
                setErrors({ password: err.error || "Login failed" });
                return;
            }

            const data = await response.json();
            console.log("Login successful:", data);

            const { message, ...userData } = data;
            store("userData", userData);

            // Redirect to dashboard or home page
            navigate("/dashboard");
        } catch (error) {
            setErrors({ password: error.message || "Sign in failed." });
            console.error("Sign in error:", error);
        }
    };

    return <SignInForm onSubmit={handleSignIn} />;
}

export default SignInPage;
